<script setup>

import { ref } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import { Inertia } from "@inertiajs/inertia";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import { useHelpers } from "@/helpers";

const props = defineProps({
  vendors: Object,
  pageSearchQuery: String,
});

const searchQuery = ref(props.pageSearchQuery);
const deactivating = ref(null);
const deactivateError = ref('');
const helpers = useHelpers();
const currentUser = usePage().props.value.auth.user;

const deactivate = (vendor) => {
  Inertia.post(route('admin.vendors.deactivate', { vendor: vendor.vendorID }), {
    preserveScroll: true,
    onError: (error) => deactivateError.value = error.vendor_id
  });
  deactivating.value = null;
};

const querySearchRef = ref(null);
const makeSearchRequest = _.debounce(() => {
  const queryParameters = route().params || {};
  if (queryParameters.page) {
    delete queryParameters.page;
  }
  queryParameters['query'] = searchQuery.value || '';
  Inertia.get(route(route().current(), queryParameters), {}, { preserveScroll: true, preserveState: true });
}, 400);

const startDeactivating = (vendor) => {
  deactivating.value = vendor;
}

const closeDeactivatedErrorModal = () => {
  deactivating.value = null;
}
</script>

<template>
  <AppLayout title="Active Vendors">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Active Vendors
      </h2>
    </template>

    <div>
      <div class="mx-auto py-10 sm:px-6 lg:px-8">
        <div class="mt-5 mb-2 grid gap-2 lg:grid-cols-2 grid-cols-1">
          <div>
            <InputLabel for="search" value="Search" class="inline mr-2" />
            <TextInput id="search" v-model="searchQuery" type="text" class="mt-1 inline" placeholder="John Doe"
              ref="querySearchRef" @keyup="makeSearchRequest" />
          </div>
          <div class="lg:text-right">
            <a :href="route('admin.vendors.create')" class="mr-2" v-if="helpers.userHasPermission(currentUser, 'vendor_database')">
              <PrimaryButton>Create New Vendor</PrimaryButton>
            </a>
            <a :href="route('admin.vendors.inactive')">
              <SecondaryButton>View Deactivated Vendors</SecondaryButton>
            </a>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full w-full text-left text-sm font-light">
            <thead class="border-b font-medium dark:border-neutral-500">
              <tr>
                <th scope="col" class="px-4 py-2">Name</th>
                <th scope="col" class="px-4 py-2">Code</th>
                <th scope="col" class="px-4 py-2">Account Number</th>
                <th scope="col" class="px-4 py-2">Notes</th>
                <th scope="col" class="px-4 py-2">Website</th>
                <th scope="col" class="px-4 py-2">Username</th>
                <th scope="col" class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
                v-for="vendor in vendors.data">
                <td class="px-4 py-2 break-words" v-text="vendor.vendorName"></td>
                <td class="px-4 py-2 break-words" v-text="vendor.vendor_code"></td>
                <td class="px-4 py-2 break-words" v-text="vendor.accountNumber"></td>
                <td class="px-4 py-2 break-words" v-text="vendor.notes"></td>
                <td class="px-4 py-2 break-words">
                  <span v-if="vendor.www">
                    <a :href="`//${vendor.www}`" v-text="vendor.www" target="_blank"></a>
                  </span>
                </td>
                <td class="px-4 py-2 break-words" v-text="vendor.username"></td>
                <td>
                  <a :href="route('admin.vendors.edit', { vendor: vendor.vendorID })" class="fa fa-pen mr-1 p-1"
                    :title="`Edit ${vendor.vendorName}`">
                  </a>
                  <button class="fa cursor-pointer transition-colors fa-toggle-on text-green-600 hover:text-green-800" @click="startDeactivating(vendor)" :disabled="deactivating"
                    :title="`Deactivate ${vendor.vendorName}`"></button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="text-center mt-5">
            <PaginationLinks :pagination="vendors" />
          </div>
        </div>
      </div>
    </div>
    <DialogModal :show="deactivating !== null" @close="closeDeactivatedErrorModal">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        You are about to deactivate <b v-text="deactivating.vendorName"></b>.
        <br>Are you sure?
      </template>

      <template #footer>
        <SecondaryButton @click="closeDeactivatedErrorModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="deactivate(deactivating)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>

  </AppLayout>
</template>
